import {jsx, ParamComponent} from 'panel';
import {selx} from 'clsx';
import uniqBy from 'lodash/uniqBy';

import {NoExtension, Section, SectionItem} from 'common/components/items-menu/types';
import {
  getCreatorNameLabel,
  getOrderedSearchContextDisplayOptions,
  getReportNameFromSearchContext,
  getSearchIconFromDisplayContext,
  getSuggestedCreators,
} from 'common/report/util/search';
import {SearchDisplayContext, SearchDisplayContextDisplayName} from 'common/report/util/search/types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ItemsMenuEl} from 'common/components/items-menu';
import {getDashboardAllowedReports} from 'common/report/util/boards';
import {findKeyByValue} from 'common/util/typescript';
import {IronContextRegistry} from 'common/report/context-registry';
import {TopCreator} from 'common/report/queries/app-api/project-top-creators';
import {IconSizeType} from 'common/constants';
import {UserIconEl} from 'common/widgets/user-icon';
import {SelectEl} from 'common/components/select';
import {ButtonEl} from 'common/components/button';
import {UserListEl} from 'common/widgets/user-list';
import {MixpanelUserItem} from 'common/widgets/user-list/types';
import {SvgIconEl} from 'common/components/svg-icon';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {BrowseTrackedEvent} from 'common/report/util/search/tracking';
import {MenuChangeEvent} from 'common/components/select/types';

import {FilterType, SearchFilter, ShareStatusFilter} from '../types';
import styles from './index.module.styl';

type BrowseFilterJsxProps = {
  params: BrowseFilterParams;
};

type BrowseFilterParams = {
  filter: SearchFilter;
  onFilterChange: (filter: SearchFilter) => void;
  onTrackEvent?: (event: BrowseTrackedEvent, props?: Record<string, any>) => void;
  isAddMode: boolean;
  userId: number;
};

type BrowseFilterState = {
  /**
   * Field used to surface applied creator filters to the top only after the creator filter menu is closed.
   * Note: these are not the applied filters, they are on param this.params.filter[FilterType.CreatorEmail]
   */
  committedCreatorFilters?: string[];
};

type OwnerFilterItem = SectionItem<ShareStatusFilter, {filterType: FilterType.ShareStatus}>;
type ReportFilterItem = SectionItem<SearchDisplayContext, {filterType: FilterType.Report}>;
type CreatorFilterItem = SectionItem<string, {filterType: FilterType.CreatorEmail; creator: TopCreator}>;

type BrowseFilterItem = OwnerFilterItem | ReportFilterItem | CreatorFilterItem;
type BrowseFilterSection = Section<any, NoExtension, BrowseFilterItem>;

const CreatorSelectTrigger = ({cmp}: {cmp: BrowseFilter}) => {
  const selectedCreators: MixpanelUserItem[] = cmp
    .getTopCreatorsFromEmails(cmp.getSelectedCreators())
    .map((creator) => ({
      id: creator.id,
      email: creator.email,
      name: creator.name,
    }));

  let suffix: string;
  if (selectedCreators.length === 0) {
    suffix = `anyone`;
  } else if (selectedCreators.length === 1) {
    suffix = selectedCreators[0].name;
  }

  return (
    <div sel={selx(styles.creatorFilterSelectTrigger)} attrs={{slot: `trigger`}}>
      <ButtonEl sel={selx(styles.triggerButton)}>
        <div sel={selx(styles.triggerLabel)}>
          <SvgIconEl sel={selx(styles.triggerIcon)} attrs={{icon: `profile`}} />
          <div>Created by{` `}</div>
          {selectedCreators.length ? (
            <UserListEl
              sel={selx(styles.triggerUsers)}
              attrs={{
                users: selectedCreators,
                size: IconSizeType.Small,
                'max-icons-displayed': 4,
                elref: `creatorTriggerUserList`,
              }}
            />
          ) : null}
          {suffix && <div sel={selx(styles.triggerLabelSuffix)}>{suffix}</div>}
          <SvgIconEl sel={selx(styles.triggerCaret)} attrs={{icon: `caret-down`}} />
        </div>
      </ButtonEl>
    </div>
  );
};

export class BrowseFilter extends ParamComponent<BrowseFilterParams, BrowseFilterState, IronContextRegistry> {
  get config() {
    return {
      contexts: [`currentProject` as const, `projectTopCreators` as const],
      template: () => {
        return (
          <div sel='.browse-filters-container'>
            <div sel={selx(styles.creatorFilterContainer)}>
              <span sel={selx(styles.creatorFilterLabel)}>Creators</span>
              <SelectEl
                sel={selx(styles.creatorFilter)}
                attrs={{
                  multiple: true,
                  'search-placeholder': `Search creators...`,
                  elref: `creatorSelect`,
                }}
                props={{sections: this.getCreatorFilterSections()}}
                on={{
                  change: (ev) => this.handleFilterChange(ev),
                  menuChange: (ev) => this.handleCreatorFilterMenuChange(ev),
                }}
              >
                <CreatorSelectTrigger cmp={this} />
              </SelectEl>
            </div>
            <ItemsMenuEl
              attrs={{multiple: true, elref: `browseFilterMenu`, 'unfocus-on-mouseleave': true}}
              props={{sections: this.getTypeFilterSections()}}
              on={{clickItem: (ev) => this.handleFilterChange(ev)}}
            />
          </div>
        );
      },
      params: {
        filter: Object,
        onFilterChange: Function,
        onTrackEvent: Function,
        isAddMode: Boolean,
        userId: Number,
      },
      defaultState: {
        committedCreatorFilters: [],
      },
    };
  }

  handleFilterChange(ev) {
    const {isSelected, filterType, value} = ev.detail.item;

    let filterValues = this.params.filter[filterType];
    if (isSelected) {
      filterValues = filterValues.filter((currVal) => currVal !== value);
    } else {
      filterValues = [...filterValues, value];
    }

    this.params.onFilterChange({
      ...this.params.filter,
      [filterType]: filterValues,
    });

    this.params.onTrackEvent?.(BrowseTrackedEvent.FilterUpdated, {
      'Changed Filter Type': filterType,
      'Changed Filter Value': value,
      'Is Filter Value Selected': !isSelected,
    });
  }

  handleCreatorFilterMenuChange(ev: MenuChangeEvent) {
    this.update({committedCreatorFilters: this.getSelectedCreators()});
    if (ev.detail.isOpen) {
      this.params.onTrackEvent?.(BrowseTrackedEvent.CreatorFilterMenuOpened);
    }
  }

  getTypeFilterSections(): BrowseFilterSection[] {
    return [
      {
        label: `Types`,
        items: this.getAvailableReports().map((value) => ({
          label: SearchDisplayContextDisplayName[findKeyByValue(SearchDisplayContext, value)],
          icon: getSearchIconFromDisplayContext(value),
          value,
          isSelected: this.params.filter[FilterType.Report].includes(value),
          filterType: FilterType.Report,
        })),
      },
    ];
  }

  getCreatorFilterSections(): BrowseFilterSection[] {
    const topCreators = this.getTopCreators();
    const orderedCreatorList = uniqBy(
      [
        ...this.getTopCreatorsFromEmails(this.state.committedCreatorFilters),
        ...getSuggestedCreators(userCookieUtils.getUserIdentityFromCookie()?.id, topCreators),
        ...topCreators,
      ],
      (creator: TopCreator) => creator.email,
    );

    return [
      {
        items: orderedCreatorList.map((creator) => ({
          label: getCreatorNameLabel(creator, this.params.userId),
          value: creator.email,
          filterType: FilterType.CreatorEmail,
          isSelected: this.getSelectedCreators().includes(creator.email),
          creator,
        })),
        iconRenderer: (item: CreatorFilterItem) => {
          return (
            <UserIconEl
              style={{'margin-right': `6px`}}
              attrs={{
                id: item.creator.id,
                email: item.creator.email,
                name: item.creator.name,
                size: IconSizeType.Small,
                'disable-tooltip': true,
                'disable-sidebar': true,
              }}
            />
          );
        },
      },
    ];
  }

  getTopCreators(): TopCreator[] {
    return this.getContext(`projectTopCreators`).getCreators();
  }

  getTopCreatorsFromEmails(emails: string[]): TopCreator[] {
    const topCreators = this.getTopCreators();
    if (!topCreators.length) {
      return [];
    }

    return emails.map((email: string) => topCreators.find((creator) => creator.email === email));
  }

  getAvailableReports() {
    let reports = getOrderedSearchContextDisplayOptions();
    if (this.params.isAddMode) {
      reports = reports.filter((report) => {
        const reportFromContext = getReportNameFromSearchContext(report);
        return getDashboardAllowedReports().includes(reportFromContext);
      });
    }
    return reports;
  }

  getSelectedCreators() {
    return this.params.filter[FilterType.CreatorEmail] || [];
  }
}

export const BrowseFilterEl = defineAndMakeJsxFactory<BrowseFilterJsxProps>(`browse-filter`, BrowseFilter);
export const BrowseFilterReact = panel2react<BrowseFilterJsxProps>(`browse-filter`, BrowseFilter);
